/** Attach photos to a timesheet entry — opens the phone camera, compresses client-side, uploads to object storage. */
import React, { useRef, useState } from 'react';
import { Camera, Images, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export const MAX_PHOTOS = 4;
export const photoUrl = (id) => `${API_URL}/api/timesheets/photos/${id}`;

async function compress(file, maxEdge = 1600, quality = 0.82) {
  if (!file.type.startsWith('image/') || file.type === 'image/heic' || file.type === 'image/heif') return file;
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return file;
  const scale = Math.min(1, maxEdge / Math.max(bmp.width, bmp.height));
  if (scale === 1 && file.size < 900 * 1024) return file;
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
  const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', quality));
  return blob ? new File([blob], (file.name || 'photo').replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file;
}

export async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append('file', await compress(file));
  const r = await fetch(`${API_URL}/api/timesheets/photos`, { method: 'POST', credentials: 'include', body: fd });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(d) || 'Upload failed');
  return d;
}

export function PhotoLightbox({ id, onClose }) {
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-2 bg-black/95 border-0" data-testid="ts-photo-lightbox">
        {id && <img src={photoUrl(id)} alt="Timesheet photo" className="w-full max-h-[85vh] object-contain rounded" />}
      </DialogContent>
    </Dialog>
  );
}

export function PhotoAttach({ photos, onChange, disabled, max = MAX_PHOTOS, big = false }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(0);
  const [view, setView] = useState(null);
  const room = max - photos.length;
  const tile = big ? 'w-28 h-28' : 'w-20 h-20';

  const pick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, room);
    e.target.value = '';
    if (!files.length) return;
    setBusy(files.length);
    const added = [];
    for (const f of files) {
      try { added.push((await uploadPhoto(f)).id); } catch (err) { toast.error(err.message); }
      setBusy((n) => n - 1);
    }
    if (added.length) onChange([...photos, ...added]);
  };
  const remove = async (id) => {
    onChange(photos.filter((p) => p !== id));
    fetch(`${API_URL}/api/timesheets/photos/${id}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
  };

  return (
    <div data-testid="ts-photo-attach">
      <div className="flex flex-wrap gap-2">
        {photos.map((id) => (
          <div key={id} className={`relative ${tile} rounded-lg overflow-hidden border bg-gray-100`} data-testid={`ts-photo-thumb-${id}`}>
            <button type="button" onClick={() => setView(id)} className="w-full h-full"><img src={photoUrl(id)} alt="" className="w-full h-full object-cover" /></button>
            {!disabled && <button type="button" onClick={() => remove(id)} aria-label="Remove photo" className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center" data-testid={`ts-photo-remove-${id}`}><X className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        {busy > 0 && <div className={`${tile} rounded-lg border bg-gray-50 flex items-center justify-center text-gray-400`} data-testid="ts-photo-uploading"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        {!disabled && room > 0 && busy === 0 && (
          <>
            <button type="button" onClick={() => camRef.current?.click()} className={`${tile} rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-500 text-gray-600 flex flex-col items-center justify-center gap-1 text-[11px] font-semibold bg-white`} data-testid="ts-photo-camera">
              <Camera className="w-5 h-5" /> Take photo
            </button>
            <button type="button" onClick={() => galRef.current?.click()} className={`${tile} rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-500 text-gray-600 flex flex-col items-center justify-center gap-1 text-[11px] font-semibold bg-white`} data-testid="ts-photo-gallery">
              <Images className="w-5 h-5" /> Gallery
            </button>
          </>
        )}
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="ts-photo-camera-input" />
      <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid="ts-photo-gallery-input" />
      {photos.length > 0 && <p className="text-[11px] text-gray-400 mt-1">{photos.length}/{max} photos</p>}
      <PhotoLightbox id={view} onClose={() => setView(null)} />
    </div>
  );
}
